import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import {
  IWarehousePurchaseCode,
  ISkuPurchaseCodeMappingResponse,
  IMappedSku,
} from "common/interface/ISkuPurchaseCode";
import { logoutIfNeeded } from "common/utils";
import { Amendment, IAmendmentsApi, SubmitAmendmentRequest } from "common/interface/Amendment";
import { getApiErrorDetails } from "common/societyAmendmentUtils";

export interface IPurchaseSkuMetadataPayload {
  companyTag?: string;
  gst?: {
    cgst?: number;
    sgst?: number;
    igst?: number;
  };
}

export interface ICreatePurchaseSkuPayload {
  skuCode: string;
  name: string;
  image?: string;
  category: string;
  unit_of_measurement: string;
  hsn?: string;
  metadata?: IPurchaseSkuMetadataPayload;
}

export interface PurchaseCodesState {
  purchaseCodes?: IWarehousePurchaseCode[];
  skuMappings: IMappedSku[];
  status: "loading" | "saving" | "deleting" | "error" | "idle";
  mappingsStatus: "loading" | "error" | "idle";
  error?: string;
  mappingsError?: string;
  amendments: IAmendmentsApi;
  currentAmendment: Amendment | null;
  amendmentStatus: "loading" | "success" | "error" | "idle" | "saving";
  amendmentError?: string;
}

const initialState: PurchaseCodesState = {
  skuMappings: [],
  status: "idle",
  mappingsStatus: "idle",
  amendments: {
    pageNo: 1,
    pageSize: 25,
    pages: 0,
    total: 0,
    data: [],
  },
  currentAmendment: null,
  amendmentStatus: "idle",
};

export const getPurchaseCodes = createAsyncThunk<IWarehousePurchaseCode[], void>(
  "purchaseCodes/all",
  async (_, thunkApi) => {
    try {
      const response = await API.get(apis.masterSkus, {
        params: { purchaseSkuFilter: "ONLY" },
      });
      return response.data as IWarehousePurchaseCode[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const deletePurchaseCode = createAsyncThunk<void, string>(
  "purchaseCodes/delete",
  async (skuCode, thunkApi) => {
    try {
      const response = await API.delete(`${apis.skuPurchaseCodes}/${skuCode}`);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);


export const getSkuMappingsForPurchaseCode = createAsyncThunk<IMappedSku[], string>(
  "purchaseCodes/getMappings",
  async (purchaseCode, thunkApi) => {
    try {
      const response = await API.get(apis.skuPurchaseCodeMappings, {
        params: { purchaseCode },
      });
      const body: ISkuPurchaseCodeMappingResponse[] = Array.isArray(response.data)
        ? response.data
        : [];
      return body[0]?.mappedSkus ?? [];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const createPurchaseSku = createAsyncThunk<void, ICreatePurchaseSkuPayload>(
  "purchaseCodes/create",
  async (payload, thunkApi) => {
    try {
      const response = await API.post(apis.createPurchaseSku, payload);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);


export const submitPurchaseCodeAmendment = createAsyncThunk(
  "purchaseCodes/submit-amendment",
  async (data: SubmitAmendmentRequest, thunkApi) => {
    try {
      const response = await API.post(apis.amendments, data);
      return response.data as Amendment;
    } catch (error) {
      const { message, code } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message, code });
    }
  }
);

export const getPurchaseCodeAmendments = createAsyncThunk<
  IAmendmentsApi,
  {
    pageNo?: number;
    pageSize?: number;
    status?: string;
    requestType?: string;
    entityId?: string;
  }
>("purchaseCodes/get-amendments", async (params, thunkApi) => {
  try {
    const response = await API.get(`${apis.amendments}/paginated`, {
      params: {
        entityType: "PURCHASE_SKU",
        pageNo: params.pageNo ?? 1,
        pageSize: params.pageSize ?? 25,
        ...(params.status ? { status: params.status } : {}),
        ...(params.requestType ? { requestType: params.requestType } : {}),
        ...(params.entityId ? { entityId: params.entityId } : {}),
      },
    });
    return response.data as IAmendmentsApi;
  } catch (error) {
    const { message } = getApiErrorDetails(error);
    logoutIfNeeded(error as any, thunkApi);
    return thunkApi.rejectWithValue({ message });
  }
});

export const getPurchaseCodeAmendmentById = createAsyncThunk<Amendment, number>(
  "purchaseCodes/get-amendment-by-id",
  async (id, thunkApi) => {
    try {
      const response = await API.get(`${apis.amendments}/${id}`);
      return response.data as Amendment;
    } catch (error) {
      const { message } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message });
    }
  }
);

export const approvePurchaseCodeAmendment = createAsyncThunk(
  "purchaseCodes/approve-amendment",
  async (
    { id, remarks, payload }: { id: number; remarks?: string; payload?: Record<string, unknown> },
    thunkApi
  ) => {
    try {
      const response = await API.put(`${apis.amendments}/${id}/approve`, {
        remarks,
        ...(payload ? { payload } : {}),
      });
      return response.data as Amendment;
    } catch (error) {
      const { message } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message });
    }
  }
);

export const rejectPurchaseCodeAmendment = createAsyncThunk(
  "purchaseCodes/reject-amendment",
  async ({ id, remarks }: { id: number; remarks: string }, thunkApi) => {
    try {
      const response = await API.put(`${apis.amendments}/${id}/reject`, { remarks });
      return response.data as Amendment;
    } catch (error) {
      const { message, code } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message, code });
    }
  }
);

export const cancelPurchaseCodeAmendment = createAsyncThunk(
  "purchaseCodes/cancel-amendment",
  async ({ id, remarks }: { id: number; remarks?: string }, thunkApi) => {
    try {
      const response = await API.put(`${apis.amendments}/${id}/cancel`, { remarks });
      return response.data as Amendment;
    } catch (error) {
      const { message } = getApiErrorDetails(error);
      logoutIfNeeded(error as any, thunkApi);
      return thunkApi.rejectWithValue({ message });
    }
  }
);

const purchaseCodesSlice = createSlice({
  name: "purchaseCodes",
  initialState,
  reducers: {
    resetAmendmentStatus: (state) => {
      state.amendmentStatus = "idle";
      state.amendmentError = "";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getPurchaseCodes.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(getPurchaseCodes.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        state.purchaseCodes = action.payload;
      })

      .addCase(getPurchaseCodes.rejected, (state, action) => {
        state.status = "error";
        const payload = action.payload;
        if (payload !== null && typeof payload === "object" && "message" in payload) {
          state.error = String(payload.message);
        }
      })
      .addCase(deletePurchaseCode.pending, (state) => {
        state.status = "deleting";
        state.error = "";
      })
      .addCase(deletePurchaseCode.fulfilled, (state) => {
        state.status = "idle";
        state.error = "";
      })
      .addCase(deletePurchaseCode.rejected, (state, action) => {
        state.status = "error";
        const payload = action.payload;
        if (payload !== null && typeof payload === "object" && "message" in payload) {
          state.error = String(payload.message);
        }
      })
      .addCase(getSkuMappingsForPurchaseCode.pending, (state) => {
        state.mappingsStatus = "loading";
        state.mappingsError = "";
      })
      .addCase(getSkuMappingsForPurchaseCode.fulfilled, (state, action) => {
        state.mappingsStatus = "idle";
        state.mappingsError = "";
        state.skuMappings = action.payload;
      })
      .addCase(getSkuMappingsForPurchaseCode.rejected, (state, action) => {
        state.mappingsStatus = "error";
        const payload = action.payload;
        if (payload !== null && typeof payload === "object" && "message" in payload) {
          state.mappingsError = String(payload.message);
        }
      })
      .addCase(createPurchaseSku.pending, (state) => {
        state.status = "saving";
        state.error = "";
      })
      .addCase(createPurchaseSku.fulfilled, (state) => {
        state.status = "idle";
        state.error = "";
      })
      .addCase(createPurchaseSku.rejected, (state, action) => {
        state.status = "error";
        const payload = action.payload;
        if (payload !== null && typeof payload === "object" && "message" in payload) {
          state.error = String(payload.message);
        }
      })

      .addCase(submitPurchaseCodeAmendment.pending, (state) => {
        state.amendmentStatus = "saving";
      })
      .addCase(submitPurchaseCodeAmendment.fulfilled, (state) => {
        state.amendmentStatus = "success";
        state.amendmentError = "";
      })
      .addCase(submitPurchaseCodeAmendment.rejected, (state, action) => {
        const payload = action.payload as { message?: string } | undefined;
        state.amendmentStatus = "error";
        state.amendmentError = payload?.message;
      })

      .addCase(getPurchaseCodeAmendments.pending, (state) => {
        state.amendmentStatus = "loading";
      })
      .addCase(getPurchaseCodeAmendments.fulfilled, (state, action) => {
        state.amendmentStatus = "idle";
        state.amendments = action.payload;
        state.amendmentError = "";
      })
      .addCase(getPurchaseCodeAmendments.rejected, (state, action) => {
        const payload = action.payload as { message?: string } | undefined;
        state.amendmentStatus = "error";
        state.amendmentError = payload?.message;
      })

      .addCase(getPurchaseCodeAmendmentById.pending, (state) => {
        state.amendmentStatus = "loading";
      })
      .addCase(getPurchaseCodeAmendmentById.fulfilled, (state, action) => {
        state.amendmentStatus = "idle";
        state.currentAmendment = action.payload;
        state.amendmentError = "";
      })
      .addCase(getPurchaseCodeAmendmentById.rejected, (state, action) => {
        const payload = action.payload as { message?: string } | undefined;
        state.amendmentStatus = "error";
        state.amendmentError = payload?.message;
        state.currentAmendment = null;
      })

      .addCase(approvePurchaseCodeAmendment.pending, (state) => {
        state.amendmentStatus = "saving";
      })
      .addCase(approvePurchaseCodeAmendment.fulfilled, (state, action) => {
        state.amendmentStatus = "success";
        state.currentAmendment = action.payload;
        state.amendmentError = "";
      })
      .addCase(approvePurchaseCodeAmendment.rejected, (state, action) => {
        const payload = action.payload as { message?: string } | undefined;
        state.amendmentStatus = "error";
        state.amendmentError = payload?.message;
      })

      .addCase(rejectPurchaseCodeAmendment.pending, (state) => {
        state.amendmentStatus = "saving";
      })
      .addCase(rejectPurchaseCodeAmendment.fulfilled, (state, action) => {
        state.amendmentStatus = "success";
        state.currentAmendment = action.payload;
        state.amendmentError = "";
      })
      .addCase(rejectPurchaseCodeAmendment.rejected, (state, action) => {
        const payload = action.payload as { message?: string } | undefined;
        state.amendmentStatus = "error";
        state.amendmentError = payload?.message;
      })

      .addCase(cancelPurchaseCodeAmendment.pending, (state) => {
        state.amendmentStatus = "saving";
      })
      .addCase(cancelPurchaseCodeAmendment.fulfilled, (state, action) => {
        state.amendmentStatus = "success";
        state.currentAmendment = action.payload;
        state.amendmentError = "";
      })
      .addCase(cancelPurchaseCodeAmendment.rejected, (state, action) => {
        const payload = action.payload as { message?: string } | undefined;
        state.amendmentStatus = "error";
        state.amendmentError = payload?.message;
      })
      ;
  },
});

export const { resetAmendmentStatus } = purchaseCodesSlice.actions;
export default purchaseCodesSlice.reducer;
